import { auth, User as FirebaseUser } from 'firebase';
import { action, computed, observable } from 'mobx';
import { Api } from '../api/Api';
import { UserProfileDto } from '../api/generated/api';

export declare interface IUserStore {
  userName: string | null;
  isAuthenticated: boolean;
  isAuthorised: boolean;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

export class UserStore implements IUserStore {

  @observable private _firebaseUser: FirebaseUser | null = null;
  @observable private _loading: boolean = true;
  @observable private _userProfile: UserProfileDto | null = null;
  @observable private _token: string | null;

  private readonly _auth: auth.Auth;
  private readonly _api: Api;

  constructor(firebaseAuth: auth.Auth, api: Api) {
    this._auth = firebaseAuth;
    this._api = api;
  }

  @action public setFirebaseUser(user: FirebaseUser | null) {
    if (user) {
      this._firebaseUser = user;
      this.refreshToken();
    } else {
      this.reset();
    }
  }

  @action public refreshToken() {
    if (this._firebaseUser) {
      this._firebaseUser.getIdToken().then((token) => {
        this.setToken(token);
      });
    }
  }

  @action public setToken(token: string | null) {
    this._api.setToken(token);
    if (this._firebaseUser && token) {
      this._token = token;
      this.refreshUserProfile();
    } else {
      this.reset();
    }
  }

  @action public refreshUserProfile() {
    if (this._token) {
      this._api.fetchUser()
      .then((result: UserProfileDto) => {
        this.setUserProfile(result);
      });
    }
  }

  @action public setUserProfile(profile: UserProfileDto | null) {
    if (this._firebaseUser && this._token && profile) {
      this._userProfile = profile;
      this._loading = false;
    } else {
      this.reset();
    }
  }

  @action public reset() {
    this._firebaseUser = null;
    this._token = null;
    this._userProfile = null;
    this._loading = false;
  }

  @computed public get isAuthenticated(): boolean {
    return Boolean(this._token);
  }

  @computed public get isAuthorised(): boolean {
    return this._userProfile
      ? Boolean(this._userProfile.isUser)
      : false;
  }

  @computed public get userName() {
    return this._firebaseUser
      ? this._firebaseUser.displayName
      : '';
  }

  @computed get token() {
    return this._token;
  }

  @computed get loading() {
    return this._loading;
  }

  @action public signOut() {
    this._auth.signOut();
  }

  @action public signIn() {
    const provider = new auth.GoogleAuthProvider();
    this._loading = true;
    this._auth.signInWithRedirect(provider);
  }

}

export function createUserStore(firebaseAuth: auth.Auth, api: Api) {
  const userStore = new UserStore(firebaseAuth, api);

  firebaseAuth.onIdTokenChanged(user => {
    userStore.setFirebaseUser(user);
  });

  return userStore;
}



// WEBPACK FOOTER //
// ./src/user/UserStore.ts